/**
 * Product Inventory Service.
 *
 * Uses the existing TInvent TypeScript client for getInventory (same as Manage License).
 * Uses raw fetch for new write operations (updateInventoryDetails, updatePendingDelivery)
 * until the TInvent client is regenerated with the new operations.
 */
import { API_BASE_URL } from '../configuration/config';
import { CSRFService } from './csrf-service';
import {
    TInvent,
    GetInventoryCommandInput,
    GetInventoryCommandOutput,
} from '@amzn/tinvent-typescript-client';
import { SentryFetchHttpHandler } from '@amzn/sentry-fetch-http-handler';
import { ProductInventoryItem } from 'components/ProductInventory/interfaces';
import { calculateSafetyStock, calculateRestockThreshold, calculateTotalSeats } from 'common/utils/inventory-calculations';
import { parseBackendError } from 'common/utils/error-utils';

const TINVENT_PROXY = `${API_BASE_URL}/tinvent`;

function getTinventClient(): [TInvent, SentryFetchHttpHandler] {
    const requestHandler = new SentryFetchHttpHandler();
    return [new TInvent({
        endpoint: TINVENT_PROXY,
        region: '*',
        credentials: { accessKeyId: '', secretAccessKey: '' },
        requestHandler,
    }), requestHandler];
}

export interface GetProductInventoryParams {
    pageSize?: number;
    pageIndex?: number;
    filters?: { [key: string]: string[] };
}

export class ProductInventoryService {
    public static instance = new ProductInventoryService();

    private client: TInvent;
    private requestHandler: SentryFetchHttpHandler;

    constructor() {
        const [client, handler] = getTinventClient();
        this.client = client;
        this.requestHandler = handler;
    }

    /**
     * Get inventory items — reuses the same TInvent client as Manage License.
     * Returns items as-is from TInvent (ProductInventoryTable handles field mapping).
     */
    public async getInventory(params?: GetProductInventoryParams): Promise<{ items: ProductInventoryItem[]; totalResults: number }> {
        const input: GetInventoryCommandInput = {
            domain: 'software',
            pageSize: params?.pageSize || 10,
            nextToken: ((params?.pageIndex || 1) - 1).toString(),
            filters: params?.filters,
        };

        return new Promise((resolve, reject) => {
            this.client.getInventory(input, (err: any, response?: GetInventoryCommandOutput) => {
                if (err || !response) {
                    reject(new Error(`Error getting inventory: ${err}`));
                } else {
                    const items: ProductInventoryItem[] = (response.items || []).map((item: any) => {
                        const lastMonthlyOrders = item.details?.lastMonthlyOrders;
                        const restockThreshold = (lastMonthlyOrders != null && item.leadTimeDays != null)
                            ? calculateRestockThreshold(lastMonthlyOrders, item.leadTimeDays) : undefined;
                        const totalSeats = item.seatsAvailable != null
                            ? calculateTotalSeats(item.seatsAvailable, item.pendingDelivery) : undefined;
                        return {
                            ...item,
                            safetyStock: (lastMonthlyOrders != null && item.leadTimeDays != null)
                                ? calculateSafetyStock(lastMonthlyOrders, item.leadTimeDays) : undefined,
                            restockThreshold,
                            totalSeats,
                            needsReorder: (totalSeats != null && restockThreshold != null)
                                ? totalSeats < restockThreshold : false,
                        };
                    });
                    resolve({
                        items,
                        totalResults: response.totalResults ?? 0,
                    });
                }
            });
        });
    }

    /**
     * Update leadTimeDays on one or more inventory items.
     * Gets CSRF token first (required for mutations through the Shop proxy).
     */
    public async updateLeadTime(taxonomyIds: string[], leadTimeDays: number): Promise<any> {
        const csrfToken = await new CSRFService().getToken();

        const response = await window.fetch(`${TINVENT_PROXY}/inventory_details`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': csrfToken,
            },
            body: JSON.stringify({ taxonomyIds, leadTimeDays }),
        });

        if (!response.ok) {
            await parseBackendError(response);
        }

        return response.json();
    }

    /**
     * Update the flexible details object on inventory items.
     * Used by the avg ordered job to write avgOrdered30/avgOrdered90.
     */
    public async updateDetails(taxonomyIds: string[], details: Record<string, any>): Promise<any> {
        const csrfToken = await new CSRFService().getToken();

        const response = await window.fetch(`${TINVENT_PROXY}/inventory_details`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': csrfToken,
            },
            body: JSON.stringify({ taxonomyIds, details }),
        });

        if (!response.ok) {
            throw new Error(`Failed to update details: ${response.status}`);
        }

        return response.json();
    }
}
